import { APP_INVENTORY_API_BASE } from "../../../../config/api";
/**
 * OpenRMAModal
 *
 * Opens a new RMA case for a faulty asset.
 * Opened from AssetDetail.jsx → RMA panel → "Open RMA case" button.
 *
 * Props:
 *   open       {boolean}
 *   onClose    {() => void}
 *   onSuccess  {(rmaCase) => void}
 *   asset      {object}  — the asset (id, serial_number, asset_type)
 *   token      {string}
 *   activeOrg  {object|null}
 *
 * API:
 *   POST /api/v1/inventory/rma
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import axios from "axios";

const API_BASE = `${APP_INVENTORY_API_BASE}`;

const FAULT_TYPES = [
  {
    value: "HARDWARE",
    label: "Hardware failure",
    desc: "Component failure, does not power on, etc.",
  },
  {
    value: "SOFTWARE",
    label: "Software issue",
    desc: "Firmware crash, data corruption, etc.",
  },
  {
    value: "PHYSICAL_DAMAGE",
    label: "Physical damage",
    desc: "Cracked screen, bent casing, liquid damage",
  },
  {
    value: "CONNECTIVITY",
    label: "Connectivity issue",
    desc: "No network, Bluetooth/WiFi failure",
  },
  { value: "OTHER", label: "Other", desc: "Does not fit the above categories" },
];

export default function OpenRMAModal({
  open,
  onClose,
  onSuccess,
  asset,
  token,
  activeOrg,
}) {
  const [faultType, setFaultType] = useState("");
  const [faultDescription, setFaultDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  useEffect(() => {
    if (open) {
      setFaultType("");
      setFaultDescription("");
      setErrors({});
      setSubmitting(false);
      setDone(false);
      setResult(null);
    }
  }, [open]);

  const validate = () => {
    const e = {};
    if (!faultType) e.faultType = "Select a fault type";
    if (!faultDescription.trim()) e.faultDescription = "Describe the fault";
    else if (faultDescription.trim().length < 10)
      e.faultDescription = "Please provide at least 10 characters";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_BASE}/rma`,
        {
          inventory_asset_id: asset.id,
          fault_type: faultType,
          fault_description: faultDescription.trim(),
        },
        { headers: authHeaders },
      );
      setResult(res.data.case);
      setDone(true);
      onSuccess(res.data.case);
    } catch (err) {
      setErrors({
        _submit: err.response?.data?.error || "Failed to open RMA case",
      });
      setSubmitting(false);
    }
  };

  if (!asset) return null;

  return (
    <Dialog
      open={open}
      onClose={done ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          pb: 0,
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#e2f8f5" }}>
              Open RMA case
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              mt={0.3}
              sx={{ color: "rgba(203, 213, 225, 0.80)" }}
            >
              {asset.serial_number} · {asset.asset_type}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              mt: -0.5,
              color: "rgba(203, 213, 225, 0.80)",
              "&:hover": {
                color: "#e2f8f5",
                background: "rgba(255,255,255,0.08)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {done && result ? (
        <>
          <DialogContent dividers sx={{ pt: 3, pb: 3 }}>
            <Box textAlign="center" py={2}>
              <CheckCircleIcon
                sx={{ fontSize: 52, color: "success.main", mb: 2 }}
              />
              <Typography variant="h6" fontWeight={700} gutterBottom>
                RMA case opened
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                A case has been filed for <strong>{asset.serial_number}</strong>
                . The OEM team will be in touch.
              </Typography>
              <Box
                sx={{
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "left",
                }}
              >
                {[
                  { label: "Case ID", value: result.id, mono: true },
                  { label: "Status", value: result.case_status },
                  {
                    label: "Fault type",
                    value: result.fault_type?.replace(/_/g, " "),
                  },
                  {
                    label: "Opened at",
                    value: result.opened_at
                      ? new Date(result.opened_at).toLocaleString("en-IN")
                      : "—",
                  },
                ].map((row, i) => (
                  <Box
                    key={i}
                    display="flex"
                    justifyContent="space-between"
                    py={0.8}
                    sx={{
                      borderBottom: i < 3 ? "1px solid" : "none",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {row.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{
                        fontFamily: row.mono ? "monospace" : "inherit",
                        fontSize: row.mono ? 11 : 12,
                      }}
                    >
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={onClose}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent dividers sx={{ pt: 2.5 }}>
            {errors._submit && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                {errors._submit}
              </Alert>
            )}

            <Stack spacing={2.5}>
              <Alert
                severity="warning"
                icon={<ReportProblemIcon />}
                sx={{ borderRadius: 2 }}
              >
                Opening an RMA case will notify the OEM team. Make sure the
                device has been unbound before shipping it back.
              </Alert>

              {/* Fault type */}
              <TextField
                select
                label="Fault type"
                size="small"
                fullWidth
                required
                value={faultType}
                onChange={(e) => {
                  setFaultType(e.target.value);
                  setErrors((p) => ({ ...p, faultType: "" }));
                }}
                error={!!errors.faultType}
                helperText={errors.faultType}
              >
                {FAULT_TYPES.map((f) => (
                  <MenuItem key={f.value} value={f.value}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {f.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {f.desc}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              {/* Fault description */}
              <TextField
                label="Fault description"
                size="small"
                fullWidth
                required
                multiline
                rows={4}
                placeholder="Describe the fault in detail — when it started, what error messages appear, what you've already tried…"
                value={faultDescription}
                onChange={(e) => {
                  setFaultDescription(e.target.value);
                  setErrors((p) => ({ ...p, faultDescription: "" }));
                }}
                error={!!errors.faultDescription}
                helperText={
                  errors.faultDescription ||
                  `${faultDescription.length} characters`
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={onClose}
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              disableElevation
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <ReportProblemIcon fontSize="small" />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                minWidth: 160,
                background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
              }}
            >
              {submitting ? "Opening case…" : "Open RMA case"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
